/* eslint-disable @typescript-eslint/naming-convention */

import {
  UserManager,
  UserManagerSettings,
  WebStorageStateStore
} from 'oidc-client-ts';

export interface SwitchedAccount {
  namespace: string;
  accountId: string;
}

export enum AuthMode {
  OAUTH = 'OAUTH2',
  INTERNAL = 'INTERNAL'
}

export interface OAuthConfig extends Record<string, unknown> {
  authMode: AuthMode;
  hasDefaultIdP?: boolean;
  oauth2?: {
    issuer: string;
    clientId: string;
    redirectUri: string;
  };
}

const SWITCHED_ACCOUNT = 'SWITCHED_ACCOUNT';
const OAUTH2_CONFIG = 'OAUTH2_CONFIG';
const wellKnownURL = import.meta.env.VITE_API_WELL_KNOWN_URL as string;

export const oAuthEnabled = (): boolean => {
  const config = localStorage.getItem(OAUTH2_CONFIG);
  if (config) {
    const parsedConfig = JSON.parse(config) as OAuthConfig;
    return parsedConfig.authMode === AuthMode.OAUTH;
  }
  return false;
};

export const hasDefaultIdP = (): boolean => {
  const config = localStorage.getItem(OAUTH2_CONFIG);
  if (config) {
    const parsedConfig = JSON.parse(config) as OAuthConfig;
    return parsedConfig.hasDefaultIdP ?? false;
  }
  return false;
};

let userManager: UserManager | null = null;

export const getUserManager = async (): Promise<UserManager | null> => {
  if (userManager) {
    return userManager;
  }
  const config = localStorage.getItem(OAUTH2_CONFIG);
  if (config) {
    const parsedConfig = JSON.parse(config) as OAuthConfig;
    const oidcSettings: UserManagerSettings = {
      authority: parsedConfig.oauth2?.issuer ?? '',
      client_id: parsedConfig.oauth2?.clientId ?? '',
      redirect_uri: parsedConfig.oauth2?.redirectUri ?? '',
      response_type: 'code',
      scope: 'offline_access irm:tenant',
      loadUserInfo: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      filterProtocolClaims: true,
      automaticSilentRenew: true
      // Important: in order to use PKCE, do not set client_secret
    };
    userManager = new UserManager(oidcSettings);
    return userManager;
  }
  return null;
};

export const clearUserManagerAndSession = async (): Promise<void> => {
  // Remove stored user from storage
  if (userManager) {
    await userManager.removeUser();
  }
  userManager = null;
};

export const fetchOauth2WellKnownConfiguration = async (): Promise<void> => {
  // Legacy Login as fallback
  const fallback = { authMode: AuthMode.INTERNAL } as OAuthConfig;
  try {
    const response = await fetch(wellKnownURL);
    if (!response.ok) {
      localStorage.setItem(OAUTH2_CONFIG, JSON.stringify(fallback));
    }
    const result = (await response.json()) as OAuthConfig;
    localStorage.setItem(OAUTH2_CONFIG, JSON.stringify(result));
  } catch {
    localStorage.setItem(OAUTH2_CONFIG, JSON.stringify(fallback));
  }
};

export const setSwitchedAccount = (switchedAccount: SwitchedAccount) => {
  localStorage.setItem(SWITCHED_ACCOUNT, JSON.stringify(switchedAccount));
};

export const getSwitchedAccount = (): SwitchedAccount | null => {
  const item = localStorage.getItem(SWITCHED_ACCOUNT);
  if (item) {
    return JSON.parse(item) as SwitchedAccount;
  }
  return null;
};

export const clearSwitchedAccount = () => {
  localStorage.removeItem(SWITCHED_ACCOUNT);
};
